// The sales pipeline: the board and its columns, stage settings, lead cards, new lead, walkthroughs.
import {
  followUp,
  type LeadRow,
  NO_STAGE_RULES,
  shortName,
  splitAddress,
  type StageRules,
  type WalkthroughRow,
} from "@steward/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type DragEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { useCreateLead, useMoveLead, usePipeline, useStageRules } from "../../api/money";
import { useSession } from "../../app/session";
import { FormField, InputText } from "../../components/fields";
import { clock, money, moneyExact, phoneText, shortDate } from "../../components/format";
import { Menu, Modal, useToast } from "../../components/overlays";
import { ago, Banner, Button, Card, IconButton, LoadError, Skeleton } from "../../components/parts";
import { errText } from "../../components/work";
import { QuoteTag, sourceWords } from "./common";
import { NewClientStart } from "./Form";

/** Cards shown in a column before "Show more". The column also scrolls inside, so the board never runs down the page. */
const COLUMN_PAGE = 20;

/** Cards a column shows before the next one is cut by the fade (owner, 2026-10-29). The rest scroll. */
const SHOWN_CARDS = 3;

/** Fades the bottom of a column only while there is more below. */
function fadeList(list: HTMLElement) {
  list.classList.toggle("fade", list.scrollTop + list.clientHeight < list.scrollHeight - 4);
}

/** The column's settings in a few words under its name. */
function rulesWords(r: StageRules, stages: { id: string; name: string }[]): string {
  const to = stages.find((s) => s.id === r.moveToStageId)?.name;
  return [
    r.followUpDays ? `Follow up in ${String(r.followUpDays)} ${r.followUpDays === 1 ? "day" : "days"}` : null,
    r.moveAfterDays && to ? `To ${to} after ${String(r.moveAfterDays)} days` : null,
    r.hideAfterDays ? `Hides after ${String(r.hideAfterDays)} days` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function PipelineBoard() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [older, setOlder] = useState(false);
  const pipe = usePipeline(company.id, older);
  const move = useMoveLead();
  const [shown, setShown] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // Where a lead was just moved, shown at once while the board reloads.
  const [moved, setMoved] = useState<Record<string, string>>({});
  const board = useRef<HTMLDivElement>(null);
  const p = pipe.data;
  const editable = can("lead.update");
  useEffect(() => {
    if (!p) return;
    setMoved((m) => {
      const left = Object.entries(m).filter(([id, to]) => p.leads.some((l) => l.id === id && l.stageId !== to));
      return left.length === Object.keys(m).length ? m : Object.fromEntries(left);
    });
  }, [p]);
  // Each column is as tall as its first 3 cards plus part of the 4th, so the cut shows there is more to scroll.
  useLayoutEffect(() => {
    const el = board.current;
    if (!el) return;
    const fit = () => {
      for (const list of el.querySelectorAll<HTMLElement>(".stage-list")) {
        const next = list.querySelectorAll<HTMLElement>(":scope > .lead")[SHOWN_CARDS];
        list.style.maxHeight = next ? `${String(Math.round(next.offsetTop + next.offsetHeight * 0.45))}px` : "";
        fadeList(list);
      }
    };
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  });
  if (pipe.error) return <LoadError error={pipe.error} retry={() => void pipe.refetch()} />;
  if (!p) return <Skeleton rows={5} />;
  const now = new Date();
  const hiddenTotal = Object.values(p.hidden ?? {}).reduce((s, n) => s + n, 0);
  const stage = p.stages.find((s) => s.id === editing);
  const leads = p.leads.map((l) => ({ ...l, stageId: moved[l.id] ?? l.stageId }));
  const dragged = leads.find((l) => l.id === dragId);
  const moveTo = (l: LeadRow, stageId: string) => {
    if (l.stageId === stageId) return;
    const from = l.stageId;
    setMoved((m) => ({ ...m, [l.id]: stageId }));
    move.mutate(
      { id: l.id, stageId },
      {
        onSuccess: () => toast(`${l.name} moved`, { undo: () => moveTo({ ...l, stageId }, from) }),
        onError: (e) => {
          setMoved((m) => ({ ...m, [l.id]: from }));
          toast(errText(e), { kind: "error" });
        },
      },
    );
  };
  const dropOn = (stageId: string) => ({
    onDragOver: (e: DragEvent) => {
      if (!dragged) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOver(stageId);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver((o) => (o === stageId ? null : o));
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setOver(null);
      setDragId(null);
      if (dragged) moveTo(dragged, stageId);
    },
  });
  return (
    <>
      <div className="pipeline" ref={board}>
        {p.stages.map((s) => {
          const here = leads.filter((l) => l.stageId === s.id);
          const limit = shown[s.id] ?? COLUMN_PAGE;
          const rules = s.rules ?? NO_STAGE_RULES;
          const words = rulesWords(rules, p.stages);
          const hidden = p.hidden?.[s.id] ?? 0;
          return (
            <section
              className={`stage${over === s.id && dragged && dragged.stageId !== s.id ? " over" : ""}`}
              key={s.id}
              aria-label={s.name}
              {...dropOn(s.id)}
            >
              <div className="stage-head">
                <div className="stage-name">
                  <b>{s.name}</b>
                  <span className="muted num small">
                    {here.length} · {money(here.reduce((t, l) => t + (l.quote?.totalCents ?? l.valueCents ?? 0), 0))}
                  </span>
                  {words ? <span className="stage-rules">{words}</span> : null}
                </div>
                {editable ? (
                  <IconButton icon="settings" label={`${s.name} column settings`} size={18} onClick={() => setEditing(s.id)} />
                ) : null}
              </div>
              <div className="stage-list" onScroll={(e) => fadeList(e.currentTarget)}>
                {here.length ? (
                  here.slice(0, limit).map((l) => (
                    <LeadCard
                      key={l.id}
                      lead={l}
                      rules={rules}
                      now={now}
                      stages={p.stages}
                      editable={editable}
                      dragging={dragId === l.id}
                      onDrag={(on) => {
                        setDragId(on ? l.id : null);
                        if (!on) setOver(null);
                      }}
                      onMove={(stageId) => moveTo(l, stageId)}
                      onQuote={() =>
                        l.quote
                          ? void navigate({ to: "/quotes/$id", params: { id: l.quote.id } })
                          : void navigate({ to: "/quotes/new", search: { leadId: l.id } })
                      }
                      onOpen={() => void navigate({ to: "/requests/$id", params: { id: l.id } })}
                    />
                  ))
                ) : (
                  <p className="muted small stage-empty">Nobody here</p>
                )}
                {here.length > limit ? (
                  <Button type="tertiary" size="small" onClick={() => setShown({ ...shown, [s.id]: limit + COLUMN_PAGE })}>
                    Show {Math.min(COLUMN_PAGE, here.length - limit)} more
                  </Button>
                ) : null}
              </div>
              {hidden ? (
                <span className="muted small">
                  {hidden} older {hidden === 1 ? "lead is" : "leads are"} hidden
                </span>
              ) : null}
            </section>
          );
        })}
      </div>
      {hiddenTotal || older ? (
        <div className="pipeline-foot">
          <span className="muted small">
            {older
              ? "Showing older leads too."
              : `${String(hiddenTotal)} older ${hiddenTotal === 1 ? "lead is" : "leads are"} off the board by column settings. They are kept.`}
          </span>
          <Button type="tertiary" size="small" onClick={() => setOlder(!older)}>
            {older ? "Hide older leads" : "Show older leads"}
          </Button>
        </div>
      ) : null}
      {stage ? (
        <StageSettings
          key={stage.id}
          stage={{ id: stage.id, name: stage.name, rules: stage.rules ?? NO_STAGE_RULES }}
          stages={p.stages}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

/** One column's own settings, saved for the company. */
function StageSettings({
  stage,
  stages,
  onClose,
}: {
  stage: { id: string; name: string; rules: StageRules };
  stages: { id: string; name: string }[];
  onClose: () => void;
}) {
  const save = useStageRules();
  const toast = useToast();
  const r = stage.rules;
  const txt = (n: number | null) => (n === null ? "" : String(n));
  const [hide, setHide] = useState(txt(r.hideAfterDays));
  const [moveAfter, setMoveAfter] = useState(txt(r.moveAfterDays));
  const [moveTo, setMoveTo] = useState(r.moveToStageId ?? "");
  const [follow, setFollow] = useState(txt(r.followUpDays));
  const [err, setErr] = useState<string | null>(null);
  const others = stages.filter((s) => s.id !== stage.id);
  const days = (s: string, what: string): number | null | string => {
    if (!s.trim()) return null;
    const n = Number(s);
    return Number.isInteger(n) && n >= 1 && n <= 3650 ? n : `${what} takes a number of days from 1 to 3650, or leave it empty.`;
  };
  const go = () => {
    const h = days(hide, "Hide after");
    const m = days(moveAfter, "Move after");
    const f = days(follow, "Follow up");
    const bad = [h, m, f].find((x) => typeof x === "string");
    if (typeof bad === "string") {
      setErr(bad);
      return;
    }
    if ((m === null) !== !moveTo) {
      setErr("To move leads on, give the days and pick the column. Or clear both.");
      return;
    }
    save.mutate(
      {
        id: stage.id,
        rules: {
          hideAfterDays: h as number | null,
          moveAfterDays: m as number | null,
          moveToStageId: moveTo || null,
          followUpDays: f as number | null,
        },
      },
      {
        onSuccess: () => {
          toast(`${stage.name} column settings saved`);
          onClose();
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  };
  return (
    <Modal
      title={`${stage.name} column settings`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={go}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">These are for this column only and for everyone in this company. Empty means off.</p>
        <InputText
          label="Follow up after"
          suffix="days"
          inputMode="numeric"
          value={follow}
          description="Cards show when to reach out. Due today turns yellow, 2 days late turns red."
          onChange={(e) => setFollow(e.target.value)}
        />
        <div className="form-grid">
          <InputText
            label="Move them on after"
            suffix="days"
            inputMode="numeric"
            value={moveAfter}
            onChange={(e) => setMoveAfter(e.target.value)}
          />
          <FormField label="Move them to">
            <select className="select" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Pick a column</option>
              {others.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <p className="muted small">Checked every 15 minutes. A moved lead starts its time in the new column.</p>
        <InputText
          label="Hide from the board after"
          suffix="days"
          inputMode="numeric"
          value={hide}
          description="Old leads drop off this column so it stays short. They are kept, and Show older leads brings them back."
          onChange={(e) => setHide(e.target.value)}
        />
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

function LeadCard({
  lead,
  rules,
  now,
  stages,
  editable,
  dragging,
  onDrag,
  onMove,
  onQuote,
  onOpen,
}: {
  lead: LeadRow;
  rules: StageRules;
  now: Date;
  stages: { id: string; name: string }[];
  editable: boolean;
  dragging: boolean;
  onDrag: (on: boolean) => void;
  onMove: (stageId: string) => void;
  onQuote: () => void;
  onOpen: () => void;
}) {
  const f = followUp(rules, lead.stageChangedAt, now);
  const contact = [phoneText(lead.phone), lead.email].filter(Boolean).join(" · ");
  const [first = "", ...rest] = (lead.assignedName ?? "").split(/\s+/);
  const extra = [
    sourceWords(lead.source),
    lead.assignedName ? `With ${shortName(first, rest.join(" "))}` : null,
    ago(lead.stageChangedAt),
  ]
    .filter(Boolean)
    .join(" · ");
  const followText = f
    ? f.daysLate < 0
      ? `Follow up ${shortDate(f.due.toLocaleDateString("en-CA"))}`
      : f.daysLate === 0
        ? "Follow up today"
        : `Follow up was due ${String(f.daysLate)} ${f.daysLate === 1 ? "day" : "days"} ago`
    : null;
  return (
    <div
      className={`lead${f?.look === "critical" ? " late" : ""}${dragging ? " dragging" : ""}`}
      draggable={editable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", lead.id);
        e.dataTransfer.effectAllowed = "move";
        onDrag(true);
      }}
      onDragEnd={() => onDrag(false)}
    >
      <div className="row between">
        <b>{lead.businessName ?? lead.name}</b>
        <Menu
          align="right"
          button={({ toggle }) => <IconButton icon="more" label={`More for ${lead.name}`} size={18} onClick={toggle} />}
        >
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  onOpen();
                }}
              >
                Open request
              </button>
              {editable
                ? stages
                    .filter((s) => s.id !== lead.stageId)
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          close();
                          onMove(s.id);
                        }}
                      >
                        Move to {s.name}
                      </button>
                    ))
                : null}
            </>
          )}
        </Menu>
      </div>
      {lead.businessName ? <span className="lead-line">{lead.name}</span> : null}
      <span className="lead-line">
        {lead.service ?? "No service yet"}
        {!lead.quote && lead.valueCents ? ` · ${money(lead.valueCents)}` : ""}
      </span>
      {lead.quote ? (
        <button type="button" className="lead-quote" onClick={onQuote}>
          <b className="num">Quote #{lead.quote.number}</b>
          <QuoteTag status={lead.quote.status} />
          <span className="num">{moneyExact(lead.quote.totalCents)}</span>
        </button>
      ) : null}
      {contact ? <span className="lead-line">{contact}</span> : null}
      {lead.address ? <span className="lead-line">{lead.address}</span> : null}
      <span className="lead-line">{extra}</span>
      {followText ? <span className={`lead-line lead-follow ${f?.look ?? ""}`}>{followText}</span> : null}
      {editable && !lead.quote ? (
        <Button type="tertiary" size="small" icon="quotes" onClick={onQuote}>
          Make a quote
        </Button>
      ) : null}
    </div>
  );
}

export function NewLead({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateLead();
  const toast = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [value, setValue] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const go = () => {
    if (!name.trim()) {
      setErr("Type a name.");
      return;
    }
    const cents = Math.round(Number(value.replace(/[$,]/g, "")) * 100);
    create.mutate(
      {
        name: name.trim(),
        phone: phone.trim() || null,
        service: service.trim() || null,
        valueCents: cents > 0 ? cents : null,
        source: "Typed in",
      },
      {
        onSuccess: () => {
          toast(`${name.trim()} added to New`);
          setName("");
          setPhone("");
          setService("");
          setValue("");
          setErr(null);
          onClose();
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  };
  return (
    <Modal
      title="New lead"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={go}>
            Add lead
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <InputText label="Name" value={name} error={err} onChange={(e) => setName(e.target.value)} />
        <InputText label="Mobile phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <InputText label="Service they want" value={service} onChange={(e) => setService(e.target.value)} />
        <InputText
          label="About how much"
          prefix="$"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <p className="muted small">The lead goes into New. Move it along as you talk with them.</p>
    </Modal>
  );
}

/** Notes and photos from a walkthrough before the bid (FEATURE-MAP 9). */
export function Walkthroughs({ quoteId }: { quoteId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["walkthroughs", quoteId],
    queryFn: () => api<{ walkthroughs: WalkthroughRow[] }>("GET", `walkthroughs?quoteId=${quoteId}`),
  });
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["walkthroughs", quoteId] });
  const add = async () => {
    await api("POST", "walkthroughs", { quoteId, day: new Date().toLocaleDateString("en-CA"), notes });
    setNotes("");
    setOpen(false);
    await refresh();
  };
  const photo = (id: string, f: File) => {
    const r = new FileReader();
    r.onload = () => {
      const data = (typeof r.result === "string" ? r.result : "").split(",")[1] ?? "";
      void api("POST", `walkthroughs/${id}/photos`, { name: f.name, mime: f.type || "image/jpeg", dataBase64: data }).then(
        refresh,
      );
    };
    r.readAsDataURL(f);
  };
  return (
    <Card
      title="Walkthrough"
      action={
        !open ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setOpen(true)}>
            Add notes
          </Button>
        ) : null
      }
    >
      {open ? (
        <div className="stack">
          <label className="field wide">
            <span className="label">What you saw (rooms, floors, special asks)</span>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="row end">
            <Button type="tertiary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!notes.trim()} onClick={() => void add()}>
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {(list.data?.walkthroughs ?? []).map((w) => (
        <div key={w.id} className="stack">
          <b>
            {shortDate(w.day)}
            {w.time ? ` at ${clock(w.time)}` : null}
            {w.who ? ` · ${w.who}` : null}
          </b>
          {w.notes ? <p>{w.notes}</p> : null}
          <span className="muted small">
            {w.photos.length} {w.photos.length === 1 ? "photo" : "photos"}
          </span>
          <label className="btn tertiary small">
            Add a photo
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) photo(w.id, f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      ))}
      {!open && !list.data?.walkthroughs.length ? <p className="muted">No walkthrough yet.</p> : null}
    </Card>
  );
}

// New client box (Jobber's) ----------------------------------------------------------------------------------------------

/** A request's address for the New client box, in parts: saved parts, else an older one line address split up. */
export function leadPlace(lead: LeadRow): Pick<NewClientStart, "street" | "street2" | "city" | "state" | "zip"> {
  const p = lead.place.city ? lead.place : splitAddress(lead.address);
  return {
    street: p.street1 ?? lead.address ?? "",
    street2: p.street2 ?? "",
    ...(p.city ? { city: p.city } : {}),
    ...(p.state ? { state: p.state } : {}),
    zip: p.zip ?? "",
  };
}
